import React, { createContext, useContext, useMemo } from 'react';

import { getImageSizeParams } from 'utils/getImageSizeParams';

import { WidgetData, WidgetSettings } from './types';

export interface ContextType<T extends { id: number }, A = WidgetSettings> {
  data: WidgetData<T>;
  settings: A;
  type: string;
  imageHeight: number;
  imageWidth: number;
  isGrid: boolean;
}

export interface Props<T extends { id: number }> {
  data: WidgetData<T>;
  settings: WidgetSettings;
  children: React.ReactNode;
  type: string;
  isGrid?: boolean;
}

const defaultContextValue = <T extends { id: number }, A = WidgetSettings>(): ContextType<T, A> => ({
  data: {} as WidgetData<T>,
  settings: {} as A,
  type: '',
  imageHeight: 0,
  imageWidth: 0,
  isGrid: false,
});

const WidgetContext = createContext<ContextType<any, any>>(defaultContextValue());

const WidgetProvider = <T extends { id: number }>({ children, data, settings, type, isGrid = false }: Props<T>) => {
  const { imageHeight, imageWidth } = getImageSizeParams(settings); // should be removed after fallback params becomes required

  const contextValue = useMemo(
    () => ({
      data,
      settings,
      type,
      isGrid,
      imageHeight, // should be removed after fallback params becomes required
      imageWidth, // should be removed after fallback params becomes required
    }),
    [data.items.length, data.hasNextPage],
  );

  return <WidgetContext.Provider value={contextValue}>{children}</WidgetContext.Provider>;
};

const useWidgetContext = <T extends { id: number }, A = WidgetSettings>(): ContextType<T, A> =>
  useContext(WidgetContext);

export { WidgetProvider, useWidgetContext };

export default React.memo(WidgetProvider) as typeof WidgetProvider;
